import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ShieldCheck, Zap, Star, ArrowRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { getBusinessInfo, getSocialProof, type BusinessInfo, type SocialProof } from "@/api/client";
import { FlashBuyButton } from "@/components/FlashBuyButton";

const slide1Person = ASSETS.slide1;

export const HeroCarousel = () => {
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [socialProof, setSocialProof] = useState<SocialProof | null>(null);

  useEffect(() => {
    void getBusinessInfo()
      .then((b) => setBusinessInfo(b))
      .catch(() => undefined);
    void getSocialProof()
      .then(setSocialProof)
      .catch(() => undefined);
  }, []);

  return (
    <section className="relative overflow-hidden py-10 md:py-28">
      {/* Ambient brand gradient backdrop */}
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-brand-soft" />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 -z-10 h-[520px] w-[520px] rounded-full bg-gradient-brand opacity-20 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 -z-10 h-[400px] w-[400px] rounded-full bg-secondary/20 blur-3xl"
      />

      <div className="container">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-4 md:space-y-6">
            {/* Top row: badges */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full border bg-white/80 backdrop-blur-sm px-4 py-1.5 shadow-sm">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                  Parceira SoluxID · AC ICP-Brasil
                </span>
              </div>
              {socialProof?.googleReviews && socialProof.googleReviews.totalRatings > 0 && (
                <div className="inline-flex items-center gap-1 rounded-full border bg-white/80 backdrop-blur-sm px-3 py-1.5 shadow-sm">
                  <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                  <span className="text-xs font-bold text-foreground tabular-nums">
                    {socialProof.googleReviews.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    · {socialProof.googleReviews.totalRatings}{" "}
                    {socialProof.googleReviews.totalRatings === 1 ? "avaliação" : "avaliações"}
                  </span>
                </div>
              )}
            </div>

            <h1 className="text-[26px] md:text-6xl font-extrabold leading-[1.1] tracking-tight text-foreground">
              Certificado Digital{" "}
              <span className="text-gradient-brand">até 12x no cartão</span>
            </h1>

            <p className="text-sm md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              e-CPF ou e-CNPJ ICP-Brasil com emissão 100% online,
              segurança máxima e atendimento humano.
            </p>

            {/* Emission promise strip — dynamic, fed by business settings */}
            {businessInfo && (
              <div className="inline-flex items-start gap-3 rounded-2xl border border-success/30 bg-success/10 px-4 py-2.5 shadow-sm">
                <Zap className="h-4 w-4 text-success shrink-0 mt-0.5" />
                <p className="text-xs text-foreground">
                  <span className="font-bold text-success">
                    {businessInfo.emission.sameDayEligible ? "Emissão hoje" : "Emissão rápida"}
                  </span>
                  {" · "}
                  {businessInfo.emission.promise}
                </p>
              </div>
            )}

            {/* Trust ribbons — compactas no mobile */}
            <div className="flex flex-wrap gap-2 md:gap-3">
              <div className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 md:px-4 md:py-2 rounded-full border shadow-card">
                <Zap className="h-3.5 w-3.5 text-success shrink-0" />
                <p className="text-xs md:text-sm font-semibold text-foreground">
                  Emissão em ~15 min
                </p>
              </div>
              <div className="hidden md:inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border shadow-card">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                </div>
                <p className="text-sm font-semibold text-foreground">
                  100% online ou presencial
                </p>
              </div>
            </div>
            <p className="hidden md:block text-xs text-muted-foreground">
              * após aprovação da validação biométrica
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-2 md:gap-3 pt-2">
              <FlashBuyButton
                size="lg"
                fullWidth
                className="text-base shadow-brand hover:shadow-brand-lg transition-all sm:w-auto"
              />
              <Button size="lg" variant="outline" className="text-base w-full sm:w-auto" asChild>
                <a href="/produtos">
                  Ver catálogo <ArrowRight className="h-4 w-4 ml-1" />
                </a>
              </Button>
              <Button size="lg" variant="ghost" className="hidden md:inline-flex text-base" asChild>
                <a href="/qual-certificado">Qual certificado preciso?</a>
              </Button>
            </div>
          </div>

          <div className="relative">
            {/* Soft glow behind image */}
            <div
              aria-hidden
              className="absolute inset-0 -z-10 translate-x-6 translate-y-6 rounded-[2rem] bg-gradient-brand opacity-30 blur-2xl"
            />
            <img
              src={slide1Person}
              alt="Pessoa usando certificado digital ICP-Brasil da Biz Certificadora em laptop"
              width="600"
              height="600"
              className="w-full h-auto rounded-2xl lg:rounded-[2rem] shadow-brand-lg object-cover max-h-[220px] md:max-h-[380px] lg:max-h-none"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
